/**
 * ============================================================================
 * 内容管理文件 - Content Management
 *
 * 【作用】全站中英文内容主入口；各页面内容在子文件中维护，此处汇总导出 content 与 getText。
 * 【修改内容】编辑对应子文件：content-nav.js（导航）、content-home.jsx（主页）、content-news.js（新闻）、
 * content-research.js（研究）、content-teaching.js（课程）、content-team.js（团队）、
 * content-publications.js（论文）、content-opening.js（开放位置）、content-contact.js（联系）、content-footer.js（页脚）。
 * 图片放在 public/images/ 下，路径用 /images/... 。
 * ============================================================================
 *
 * 【新架构说明】（第三人称客观注释）
 * - 内容以“页面命名空间”为顶层键：nav / home / news / research / teaching / team / publications / openings / contact / footer。
 * - 子文件需导出同名对象：例如 content-home.jsx 导出 { home: { ... } }。
 * - 全站获取文案统一走 getText('home.hero.titlePrefix', lang) 这种路径。
 * - 不再提供历史兼容别名（例如 hero.*），以避免长期维护双路径导致的混乱。
 */

// 导入各个页面的内容模块（新架构：每个模块导出同名命名空间对象）
import { navContent } from './content-nav';
import { homeContent } from './content-home';
import { newsContent } from './content-news';
import { researchContent } from './content-research';
import { teachingContent } from './content-teaching';
import { teamContent } from './content-team';
import { publicationsContent } from './content-publications';
import { openingsContent } from './content-opening';
import { contactContent } from './content-contact';
import { footerContent } from './content-footer';

/**
 * ============================================================================
 * ############## 内容汇总（强一致命名空间） ##################################
 * ============================================================================
 *
 * 【维护约定】（第三人称客观注释）
 * - 每个 content-*.js(x) 文件导出对象必须包含且仅包含对应命名空间键：
 *   - content-home.jsx => export const homeContent = { home: { ... } }
 *   - content-research.js => export const researchContent = { research: { ... } }
 * - 若模块未遵循该约定，getText 的路径将无法命中。
 */
export const content = {
  nav: navContent?.nav || navContent,
  home: homeContent?.home || homeContent,
  news: newsContent?.news || newsContent,
  research: researchContent?.research || researchContent,
  teaching: teachingContent?.teaching || teachingContent,
  team: teamContent?.team || teamContent,
  publications: publicationsContent?.publications || publicationsContent,
  openings: openingsContent?.openings || openingsContent,
  contact: contactContent?.contact || contactContent,
  footer: footerContent?.footer || footerContent
};

/**
 * ============================================================================
 * ############## getText：获取多语言内容的辅助函数 #############################
 * ============================================================================
 *
 * @param {string} path - 内容路径，点号分隔，支持数组索引，如 'nav.items.0.label'
 * @param {string} lang - 语言代码 'en' | 'cn'，默认 'en'
 * @returns {any} 对应语言的内容；若为对象且含 en/cn 则返回该语言值；数组则逐项按语言转换；否则返回空字符串
 *
 * 【示例】（第三人称客观注释）
 * - getText('home.hero.titlePrefix', 'en') => "Designing the"
 * - getText('research.sections.0.title', 'cn') => 某个研究条目的中文标题
 *
 * 【返回策略】（第三人称客观注释）
 * - 命中 {en, cn} 对象：返回 obj[lang]，若缺失则回退 obj.en。
 * - 命中数组：逐项将其中的 {en, cn} 字段解包为对应语言值（兼容数组元素为对象的情况）。
 * - 命中 JSX/字符串/数值：直接返回。
 */
export const getText = (path, lang = 'en') => {
  const keys = path.split('.');
  let obj = content;

  for (const key of keys) {
    if (obj === null || obj === undefined) return '';
    obj = obj[key];
  }

  if (obj === null || obj === undefined) return '';

  // 数组：逐项处理语言字段
  if (Array.isArray(obj)) {
    return obj.map(item => {
      if (typeof item === 'object' && item !== null) {
        // 数组元素本身若为 {en/cn}，直接解包
        if (item[lang] !== undefined) return item[lang];
        if (item.en !== undefined) return item.en;

        // 否则对对象内字段进行浅层语言解包（保持原逻辑的可预测性）
        const result = {};
        for (const k in item) {
          const v = item[k];
          if (typeof v === 'object' && v !== null && (v.en !== undefined || v.cn !== undefined)) {
            result[k] = v[lang] !== undefined ? v[lang] : (v.en !== undefined ? v.en : v);
          } else {
            result[k] = v;
          }
        }
        return result;
      }
      return item;
    });
  }

  // 对象：若含 en/cn 语言字段则按语言返回
  if (typeof obj === 'object' && obj !== null) {
    if (obj[lang] !== undefined) return obj[lang];
    if (obj.en !== undefined) return obj.en;
  }

  return obj;
};
